/**
 * Fetches the admin `/users` and `/tracks` lists once and exposes them as the
 * id → label lookup the approvals pages render beside each id — and, for the
 * pages whose filters pick from those lists (audit log), as the raw rows.
 *
 * Failure is deliberately silent: the labels are garnish over ids that are
 * already on screen, so a failed lookup must not blank the queue or raise a
 * banner over a page whose own data loaded fine.
 */
import { useEffect, useMemo, useState } from 'react';
import type {
  TfTrackRow,
  TfUserRow,
  UserListResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { hub } from './hub-client';
import { buildPrincipalLabels, type PrincipalLabels } from './principal-labels';

export interface PrincipalLists {
  users: TfUserRow[];
  tracks: TfTrackRow[];
}

/**
 * The raw lists. `users: false` skips `GET /users` — it is admin-only, and a
 * page a non-admin can open should not fire a request that is certain to 403
 * (the hub audits that denial into the very trail the audit page shows).
 */
export function usePrincipalLists(opts: { users?: boolean } = {}): PrincipalLists {
  const wantUsers = opts.users ?? true;
  const [users, setUsers] = useState<TfUserRow[]>([]);
  const [tracks, setTracks] = useState<TfTrackRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (hub.tracksList() as Promise<TfTrackRow[]>)
      .then((rows) => {
        if (!cancelled) setTracks(rows);
      })
      .catch(() => {
        // Ids keep rendering unlabelled — see the module docblock.
      });
    if (wantUsers) {
      (hub.usersList() as Promise<UserListResponse>)
        .then((res) => {
          if (!cancelled) setUsers(res.users);
        })
        .catch(() => {
          // Not an admin (403) or hub unreachable — ids it is.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [wantUsers]);

  return { users, tracks };
}

export function usePrincipalLabels(): PrincipalLabels {
  const { users, tracks } = usePrincipalLists();
  return useMemo(() => buildPrincipalLabels(users, tracks), [users, tracks]);
}
